// Moteur analytique (doc §14-15) : performance des triturations (usine) vs performance fournisseurs.
import { fetchReceptions, fetchOfs, fetchLotsPlanifiables, decompte, huileTheorique, nomTiers, familleLot, labelQualite } from './dataProvider';

const rdtTheo = (r) => (decompte(r).poidsNet > 0 ? huileTheorique(r) / decompte(r).poidsNet : 0);

// TABLEAU 1 — une ligne par OF terminé (Trituration Groupe).
export async function performanceTriturations() {
  const ofs = (await fetchOfs()).filter((o) => o.statut === 'Terminé');
  const rows = await fetchReceptions();
  return ofs.map((o) => {
    const lots = o.lots.map((id) => rows.find((r) => r.id === id)).filter((r) => r && r.parcours === 'fournisseur');
    const kg = lots.reduce((s, r) => s + decompte(r).poidsNet, 0);
    const huileTheo = lots.reduce((s, r) => s + huileTheorique(r), 0);
    const huileReelle = lots.reduce((s, r) => s + (Number(r.huileExtraite) || 0), 0);
    const cout = lots.reduce((s, r) => s + (decompte(r).enAttenteRendement ? 0 : decompte(r).montant), 0);
    const rdtTheoPond = kg > 0 ? huileTheo / kg : 0;
    const rdtReel = kg > 0 ? huileReelle / kg : 0;
    const coef = rdtTheoPond > 0 ? rdtReel / rdtTheoPond : null;
    return {
      of: o.numero, date: o.termineLe || o.date, kg, cout, rdtTheoPond, huileTheo, huileReelle, rdtReel,
      ecartPts: (rdtReel - rdtTheoPond) * 100, bonusKg: huileReelle - huileTheo, coef,
      coutTheoKg: huileTheo > 0 ? cout / huileTheo : null, coutReelKg: huileReelle > 0 ? cout / huileReelle : null,
      gainRendement: huileTheo > 0 && huileReelle > 0 ? (huileReelle - huileTheo) * (cout / huileTheo) : 0,
      lots: lots.map((r) => {
        const theo = rdtTheo(r);
        const ajuste = coef != null ? theo * coef : null;
        return { id: r.id, tiersId: r.tiersId, kg: decompte(r).poidsNet, rdtTheo: theo, rdtAjuste: ajuste, bonusIndicatif: ajuste != null ? decompte(r).poidsNet * (ajuste - theo) : null, mixe: lots.length > 1 };
      }),
    };
  });
}

// TABLEAU 2 — agrégation analytique par fournisseur (écart pondéré, volatilité, score, classement).
export async function performanceFournisseurs() {
  const trit = await performanceTriturations();
  const parTiers = {};
  trit.forEach((t) => t.lots.forEach((l) => {
    if (l.rdtAjuste == null) return;
    const p = (parTiers[l.tiersId] = parTiers[l.tiersId] || { tiersId: l.tiersId, nom: nomTiers(l.tiersId), kg: 0, livraisons: 0, sRdt: 0, sEcart: 0, ecarts: [], bonus: 0 });
    const ecart = l.rdtAjuste - l.rdtTheo;
    p.kg += l.kg; p.livraisons += 1; p.sRdt += l.kg * l.rdtAjuste; p.sEcart += l.kg * ecart; p.ecarts.push(ecart * 100); p.bonus += l.bonusIndicatif;
  }));
  const liste = Object.values(parTiers).map((p) => {
    const moy = p.ecarts.reduce((s, e) => s + e, 0) / p.ecarts.length;
    const vol = Math.sqrt(p.ecarts.reduce((s, e) => s + (e - moy) ** 2, 0) / p.ecarts.length);
    const ecartPond = (p.sEcart / p.kg) * 100;
    return { ...p, rdtMoyen: (p.sRdt / p.kg) * 100, ecartMoyen: moy, ecartPondere: ecartPond, volatilite: vol, score: Number((ecartPond * 10 - vol * 2).toFixed(1)) };
  });
  liste.sort((a, b) => b.score - a.score).forEach((p, i) => (p.classement = i + 1));
  return liste;
}

// Suggestion d'OF : RÈGLE D'OR — jamais de lampante avec des nobles ; clients seuls (voyages du même client groupés).
export async function suggererOfs(filtre = {}) {
  const lots = (await fetchLotsPlanifiables()).filter((r) => (!filtre.parcours || r.parcours === filtre.parcours) && (!filtre.date || r.date === filtre.date));
  const groupes = [];
  ['noble', 'lampante'].forEach((fam) => {
    const f = lots.filter((r) => familleLot(r) === fam && r.parcours === 'fournisseur');
    if (f.length) groupes.push({ titre: `Achats ${fam === 'lampante' ? 'LAMPANTE — session dédiée isolée' : 'nobles (vierge / extra-vierge)'}`, lots: f });
    const clients = lots.filter((r) => familleLot(r) === fam && r.parcours === 'client');
    const parClient = {};
    clients.forEach((r) => (parClient[r.tiersId] = parClient[r.tiersId] || []).push(r));
    Object.entries(parClient).forEach(([tid, rs]) => groupes.push({ titre: `Prestation ${nomTiers(tid)}${rs.length > 1 ? ` — ${rs.length} voyages groupés` : ''}${fam === 'lampante' ? ' · LAMPANTE isolée' : ''}`, lots: rs }));
  });
  return { lots: lots.length, groupes };
}

export const texteSuggestionOfs = ({ lots, groupes }) => {
  if (!lots) return 'Aucun lot « En attente » ne correspond : rien à planifier pour l’instant.';
  const fmt = (v) => new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(v);
  return [
    `**Proposition de ${groupes.length} OF** pour ${lots} lot(s) en attente — règle d’or appliquée : lampante et vierge/extra-vierge jamais mélangées, clients jamais mêlés à d’autres tiers.`,
    ...groupes.map((g, i) => `${i + 1}. **OF ${g.titre}** — ${g.lots.length} lot(s), ${fmt(g.lots.reduce((s, r) => s + decompte(r).poidsNet, 0))} kg : ${g.lots.map((r) => `${r.id} (${labelQualite(r.qualite) || 'qualité ?'}, ${fmt(decompte(r).poidsNet)} kg)`).join(', ')}`),
    '',
    'Pour lancer : Planning atelier → cochez les lots indiqués → « Créer l’OF » (le système bloque tout mélange de filières).',
  ].join('\n');
};
